<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    >
      <template #label>
        <slot name="label" />
      </template>
    </AM3InputLabel>
    <div
      class="am3-input-wrapper bg-surface-light  flex h-11 flex-row items-center justify-between rounded-md  pl-3"

      :data-test-id="dataTestId ? `${dataTestId}-wrapper` : 'slug-input-wrapper'"
    >
      <span
        v-if="prefix"
        class="text-sm text-secondary border-y border-l bg-surface cursor-pointer whitespace-nowrap -my-6 -ml-3 px-3 h-full flex flex-col items-center justify-center  rounded-l-md shrink-0"
        @click="focusInput"
      >
        {{ prefix }}
      </span>

      <input
        :id="inputId"
        ref="inputRef"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        type="text"
        class="am3-input bg-transparent outline-offset-0 outline-1 rounded-r-md border-l border-y border-r px-3 py-2.5 text-sm h-11 w-full outline-none relative"
        :aria-label="ariaLabel || String(label) || 'Slug'"
        :data-test-id="dataTestId ? `${dataTestId}-input` : 'slug-input'"
        :class="[
          disabled ? disabledClasses : '',
          (validationState === false || typeof validationState === 'string')
            ? invalidClasses
            : borderClasses,
        ]"
        @input="handleInput"
        @keyup="handleInput"
        @blur="handleBlur"
      >
    </div>
    <AM3InputError
      :error="validationState"
      :data-test-id="dataTestId ? `${dataTestId}-error` : 'slug-input-error'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

interface Props {
  modelValue?: string
  prefix?: string
  label?: string | false
  placeholder?: string
  disabled?: boolean
  required?: boolean
  validate?: (value: string) => boolean | string
  dataTestId?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  prefix: '',
  label: '',
  placeholder: 'enter-your-slug-here',
  disabled: false,
  required: false,
  validate: () => true,
  dataTestId: 'slug-input',
  ariaLabel: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
}>()

const inputId = ref(`am3-slug-${uuidv4()}`)
const inputRef = ref<HTMLInputElement | null>(null)

const borderClasses = 'focus-within:outline-brand hover:border-brand border-surface-dark border border-solid'
const disabledClasses = 'text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none'
const invalidClasses = 'error border border-solid border-[var(--font-colour-warning)] focus-within:outline-[var(--font-colour-warning)] hover:border-[var(--font-colour-warning)]'

const {
  validationState,
  runValidation: _runValidation,
  resetValidation,
  handleBlur: _handleBlur,
} = useFormValidation(props.validate, props.modelValue, props.required)

const focusInput = () => {
  if (inputRef.value) {
    inputRef.value.focus()
  }
}

// Applied on every keystroke — permissive so mid-word hyphens are allowed while typing
const sanitizeInteractive = (value: string): string => {
  if (!value) return ''
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
}

// Applied on blur — strips leading/trailing hyphens left over from typing
const sanitizeFinal = (value: string): string => {
  return sanitizeInteractive(value).replace(/^-+|-+$/g, '')
}

const handleInput = (event: Event) => {
  const raw = (event.target as HTMLInputElement).value
  const sanitized = sanitizeInteractive(raw)
  emit('update:modelValue', sanitized)
  if (inputRef.value && sanitized !== raw) {
    const cursor = inputRef.value.selectionStart ?? sanitized.length
    inputRef.value.value = sanitized
    // Restore cursor, accounting for any removed characters
    const newCursor = Math.min(cursor, sanitized.length)
    inputRef.value.setSelectionRange(newCursor, newCursor)
  }
}

const handleBlur = () => {
  const final = sanitizeFinal(props.modelValue ?? '')
  if (final !== props.modelValue) {
    emit('update:modelValue', final)
    if (inputRef.value) inputRef.value.value = final
  }
  _handleBlur(final)
  emit('change', final)
}

const runValidation = (value = props.modelValue, checkRequired = props.required) => {
  return _runValidation(value, checkRequired)
}

defineExpose({ runValidation, resetValidation })
</script>
